import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi'
import API from '../../../services/API'

const POLL_MS = 20000

// Public, unauthenticated page (§5.2) — reachable by anyone with the link,
// no AmbuNet login. Deliberately coarse: a status headline and, only while
// the crew is Mobile, a single point-in-time ETA estimate. No map, no other
// patients' information, and it stops being useful (and stops polling) once
// the journey is complete or the link has expired.
const JourneyTracking = () => {
  const { token } = useParams()
  const [state, setState] = useState({ loading: true, error: null, data: null })
  const pollRef = useRef(null)

  const load = async () => {
    try {
      const res = await API.get(`/public/pts/track/${token}`)
      if (res.status === 200) {
        setState({ loading: false, error: null, data: res.data })
      } else if (res.status === 410) {
        setState({ loading: false, error: 'expired', data: null })
        clearInterval(pollRef.current)
      } else {
        setState({ loading: false, error: 'not-found', data: null })
        clearInterval(pollRef.current)
      }
    } catch (e) {
      setState({ loading: false, error: 'not-found', data: null })
      clearInterval(pollRef.current)
    }
  }

  useEffect(() => {
    load()
    pollRef.current = setInterval(load, POLL_MS)
    return () => clearInterval(pollRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (state.data && !state.data.estimatedArrivalAt && /complete/i.test(state.data.headline || '')) {
      clearInterval(pollRef.current)
    }
  }, [state.data])

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="#F1F5F9"
      p={2}
    >
      <Paper elevation={2} sx={{ maxWidth: 420, width: '100%', p: 4, borderRadius: 2 }}>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <LocalTaxiIcon sx={{ fontSize: 40, color: '#0F766E' }} />
          <Typography variant="h6" fontWeight={800}>
            Patient Transport Tracking
          </Typography>

          {state.loading && <CircularProgress size={28} />}

          {state.error === 'expired' && (
            <Typography color="text.secondary">
              This tracking link has expired.
            </Typography>
          )}
          {state.error === 'not-found' && (
            <Typography color="text.secondary">
              We couldn't find this tracking link. It may be out of date —
              please contact us if you need an update.
            </Typography>
          )}

          {state.data && (
            <>
              <Typography variant="body1">{state.data.headline}</Typography>
              {state.data.estimatedArrivalAt && (
                <Typography variant="body2" color="text.secondary">
                  Estimated arrival around{' '}
                  {moment(state.data.estimatedArrivalAt).format('HH:mm')}
                </Typography>
              )}
              {(state.data.pickupLabel || state.data.dropoffLabel) && (
                <Typography variant="caption" color="text.secondary">
                  {[state.data.pickupLabel, state.data.dropoffLabel]
                    .filter(Boolean)
                    .join(' → ')}
                </Typography>
              )}
            </>
          )}

          <Typography variant="caption" color="text.disabled">
            This page updates automatically and does not require a login.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  )
}

export default JourneyTracking
